/**
 * Eating-type diagnosis for the acquisition funnel.
 *
 * The RULES live in `funnel_config.diagnosis` (a DB row), not here, because 13 of
 * the 21 questions are still unwritten and native has no OTA — every copy or
 * weighting change would otherwise cost a store release. This module is only the
 * engine that applies them, which is why it is pure and exhaustively tested: the
 * data changes often, the arithmetic must not.
 */

export interface DiagnosisBucket {
  id: string;
  title: string;
  body?: string;
  bullets?: string[];
  /**
   * The personal plan's lines for this type, shown after signup and not on the
   * result screen. All optional: a type without one gets a plan without that
   * section.
   *
   * Small first-step tips only. The plan and its email reach every new member
   * and the email leaves the app, so no line may teach a paid course's
   * technique — the method lives in the lessons.
   */
  planSteps?: string[];
  /** How the app helps with this type: the lessons, Tovi, the trackers. */
  planAppValue?: string[];
  /** One line pointing at a course for this type. Never in the email. */
  planCourse?: string;
}

export interface DiagnosisRules {
  buckets: DiagnosisBucket[];
  /** weights[stepId][answerValue][bucketId] = points */
  weights: Record<string, Record<string, Record<string, number>>>;
  /** Bucket ids in priority order, used to settle an exact tie. */
  tiebreak?: string[];
  /** The result screen's button label. Absent or blank → `DEFAULT_DIAGNOSIS_CTA`. */
  cta?: string;
  /**
   * The short "working it out" screen before the result: its heading and its
   * three lines, by position. Each blank slot → the built-in text for that slot.
   */
  analysis?: { title?: string; lines?: string[] };
}

export const DEFAULT_DIAGNOSIS_CTA = "בונים את התוכנית שלי";

/** The label the result screen's button actually shows. */
export function diagnosisCta(rules: DiagnosisRules | null | undefined): string {
  return rules?.cta?.trim() || DEFAULT_DIAGNOSIS_CTA;
}

export const DEFAULT_ANALYSIS_TITLE = "רגע, מחברים את הנקודות";
export const DEFAULT_ANALYSIS_LINES = ["בודקים את התשובות שלך", "מזהים את דפוס האכילה", "האבחון מוכן"] as const;

/**
 * What the analysis screen shows. Always exactly three lines: the screen ticks
 * one per beat and its ring counts "1 מתוך 3", so the count is part of the
 * screen's timing, not of its copy.
 */
export function analysisCopy(rules: DiagnosisRules | null | undefined): { title: string; lines: string[] } {
  const custom = rules?.analysis;
  return {
    title: custom?.title?.trim() || DEFAULT_ANALYSIS_TITLE,
    lines: DEFAULT_ANALYSIS_LINES.map((fallback, i) => {
      const line = custom?.lines?.[i];
      return (typeof line === "string" && line.trim()) || fallback;
    }),
  };
}

/** A step id → the answer(s) the user gave. Multi-selects arrive as arrays. */
export type FunnelAnswers = Record<string, string | string[] | null | undefined>;

export interface DiagnosisResult {
  bucket: DiagnosisBucket | null;
  scores: Record<string, number>;
}

/**
 * Scores every bucket and returns the winner.
 *
 * Resolution order, and each rule exists because the alternative is worse:
 *  1. Highest total score.
 *  2. On a tie, the earliest id in `tiebreak`. Without this the winner would be
 *     whatever order Object.keys happened to produce — a diagnosis that changes
 *     between runs on identical answers.
 *  3. On a tie with no tiebreak entry, the earliest bucket in `buckets`.
 *
 * Returns `bucket: null` only when there is genuinely nothing to show (no
 * buckets configured). A user who answered nothing still gets the first bucket
 * rather than a blank screen — every bucket scores 0, and rule 2 picks one.
 * Showing *a* result is the product requirement; the paywall follows it.
 *
 * Unknown step ids and unknown answer values are ignored rather than throwing:
 * the config is edited by hand in an admin screen, and a typo there must not
 * blank the funnel for every user.
 */
export function scoreDiagnosis(
  answers: FunnelAnswers | null | undefined,
  rules: DiagnosisRules | null | undefined
): DiagnosisResult {
  const buckets = rules?.buckets ?? [];
  if (!buckets.length) return { bucket: null, scores: {} };

  const scores: Record<string, number> = {};
  for (const b of buckets) scores[b.id] = 0;

  const weights = rules?.weights ?? {};

  for (const [stepId, rawAnswer] of Object.entries(answers ?? {})) {
    const stepWeights = weights[stepId];
    if (!stepWeights) continue;

    const values = Array.isArray(rawAnswer)
      ? rawAnswer
      : rawAnswer == null
        ? []
        : [rawAnswer];

    for (const value of values) {
      const perBucket = stepWeights[value];
      if (!perBucket) continue;

      for (const [bucketId, points] of Object.entries(perBucket)) {
        // Only score buckets that actually exist, so a stale weight left behind
        // after a bucket was renamed cannot invent a winner with no copy.
        if (!(bucketId in scores)) continue;
        if (typeof points !== "number" || !Number.isFinite(points)) continue;
        scores[bucketId] += points;
      }
    }
  }

  const order = rules?.tiebreak ?? [];
  const rank = (id: string) => {
    const i = order.indexOf(id);
    // Anything absent from tiebreak sorts after everything in it, then by the
    // bucket's own declaration order.
    return i === -1 ? order.length + buckets.findIndex((b) => b.id === id) : i;
  };

  let winner = buckets[0];
  for (const b of buckets) {
    if (scores[b.id] > scores[winner.id]) {
      winner = b;
    } else if (scores[b.id] === scores[winner.id] && rank(b.id) < rank(winner.id)) {
      winner = b;
    }
  }

  return { bucket: winner, scores };
}

/**
 * She reached the result without answering anything that scores.
 *
 * `scoreDiagnosis` always returns a bucket: with every score at zero the
 * tiebreak decides, so an empty quiz produces a confident "סוג האכילה שלך:
 * אכילה רגשית" that was read off nothing. Reachable without doing anything
 * wrong — a hand-typed URL, cleared storage mid-quiz, or an out-of-range guard
 * forwarding a stale index to the result.
 *
 * Naming her eating type from no evidence is the one thing the result screen
 * must not do: the whole funnel is an argument that the diagnosis is derived
 * from HER answers, and the paywall two taps later charges money on the
 * strength of it.
 *
 * Only when weights exist at all — a config with buckets and no weights scores
 * everyone by tiebreak BY DESIGN, and bouncing those users would be a
 * regression, not a guard.
 */
export function scoredNothing(
  answers: FunnelAnswers | null | undefined,
  rules: DiagnosisRules | null | undefined,
  /**
   * The step ids she could have answered by now. When the result is a step in
   * the list, only the questions before it count: a scored question placed
   * after it (an admin mistake the editor warns about) or hidden by a branch
   * can never be answered in time, and treating it as missing evidence sends
   * her back to the start on every lap — forever. Omitted: every weighted id.
   */
  answerable?: ReadonlySet<string>
): boolean {
  const ids = Object.keys(rules?.weights ?? {}).filter((id) => !answerable || answerable.has(id));
  if (ids.length === 0) return false;
  return !ids.some((id) => {
    const v = answers?.[id];
    return Array.isArray(v) ? v.length > 0 : v != null && String(v).trim() !== "";
  });
}
